/**
 * A tranca — impede que a mesma ação rode duas vezes ao mesmo tempo.
 *
 * Existe porque a atendente clicou três vezes em "Buscar" achando que nada
 * tinha acontecido (28/08/2026). Em "Buscar" três cliques são desperdício; em
 * "Lançar" e "Carimbar visita" são três vendas. O véu de espera esconde o botão
 * do mouse, mas não vale contra dois cliques no MESMO quadro, contra Enter
 * repetido no teclado, nem contra um duplo-clique que dispara antes de o React
 * repintar. Esta trava barra na origem.
 *
 * ⚠️ POR QUE NÃO É `useState`. `setOcupado(true)` só chega ao DOM no próximo
 * quadro. Dois cliques no mesmo quadro leem `ocupado === false` os dois e
 * passam os dois. A tranca é síncrona: o segundo clique já encontra a chave
 * tomada.
 *
 * Isto NÃO substitui idempotência no servidor (invariante 4 do CLAUDE.md: chave
 * única no banco). É a primeira barreira, não a última — a rede pode duplicar
 * um POST sem clique nenhum.
 *
 * Pura de propósito: o Vitest deste repo roda em `environment: 'node'`, sem
 * DOM. A lógica que importa se testa aqui; o `useAgir` só a embrulha em React.
 */

/** Quando a ação não tem nome — a tela só faz uma coisa por vez. */
export const CHAVE_PADRAO = '';

export type Tranca = ReturnType<typeof criarTranca>;

export function criarTranca() {
  const rodando = new Set<string>();

  return {
    /**
     * Toma a chave. `false` quer dizer que já havia alguém nela — quem chamou
     * deve DESISTIR, não esperar.
     */
    pegar(chave: string = CHAVE_PADRAO): boolean {
      if (rodando.has(chave)) return false;
      rodando.add(chave);
      return true;
    },

    /** Devolve a chave. Soltar o que não está preso não é erro. */
    soltar(chave: string = CHAVE_PADRAO): void {
      rodando.delete(chave);
    },

    /** Esta chave está rodando? */
    presa(chave: string = CHAVE_PADRAO): boolean {
      return rodando.has(chave);
    },

    /** Qualquer coisa rodando? É o que apaga a tela inteira. */
    ocupada(): boolean {
      return rodando.size > 0;
    },

    /** As chaves presas agora — a leitura que vira estado do React. */
    presas(): ReadonlySet<string> {
      return new Set(rodando);
    },

    /**
     * Solta tudo. Para desmontagem: uma tela que sai do ar com chave presa
     * deixaria a tranca suja se ela fosse compartilhada entre montagens.
     */
    soltarTudo(): void {
      rodando.clear();
    },
  };
}
